/**
 * DealCreatedSuccessDialog - Modal de sucesso após criação de negócio
 *
 * Espelha 100% o visual e leiaute do TaskCreatedSuccessDialog usado no módulo
 * de Tarefas, adaptando o destaque central para o título do negócio + ID
 * truncado em 8 caracteres uppercase (padrão de exibição de UUIDs do projeto).
 *
 * CHANGELOG:
 *   RELEASE: 2026-08-12 v1.2.0 - Ao clicar em "Compartilhar proposta" o modal de
 *            sucesso é fechado (evita o compartilhamento abrir atrás dele).
 *   RELEASE: 2026-08-12 v1.1.0 - Quando o negócio nasce com proposta automática,
 *            exibe o número da proposta e o botão "Compartilhar proposta", que
 *            reaproveita o ProposalShareDialog já usado no modal de detalhes.
 *   RELEASE: 2026-05-12 v1.0.0 - Criação inicial replicando o padrão de Tarefas
 */

import { useState } from 'react';
import { Check, Eye, Share2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { useProposalDetails, useProposalPdf } from '@/hooks/useDealProposals';
import { ProposalShareDialog } from '@/components/crm/deals/modal/tabs/proposals/ProposalShareDialog';
import { ChatConversationModal } from '@/components/tenant/kanban/ChatConversationModal';

interface DealCreatedSuccessDialogProps {
  /** Controla visibilidade do modal */
  open: boolean;
  /** Callback para mudar estado de abertura */
  onOpenChange: (open: boolean) => void;
  /** ID (UUID) do negócio recém-criado */
  dealId: string;
  /** Título do negócio recém-criado */
  dealTitle: string;
  /** Callback ao clicar em "Visualizar Negócio" */
  onViewDeal: () => void;
  /** Proposta gerada automaticamente junto com o negócio (opcional) */
  proposal?: { id: string; publicUrl: string | null } | null;
}

export function DealCreatedSuccessDialog({
  open,
  onOpenChange,
  dealId,
  dealTitle,
  onViewDeal,
  proposal,
}: DealCreatedSuccessDialogProps) {
  // Trunca o UUID para 8 chars uppercase, conforme padrão Core do projeto
  const shortId = dealId ? dealId.slice(0, 8).toUpperCase() : '';

  // Compartilhamento da proposta gerada automaticamente
  const [shareOpen, setShareOpen] = useState(false);
  const [chatConversationId, setChatConversationId] = useState<string | null>(null);

  // Detalhes da proposta (contato/telefone/PDF) — só busca quando há proposta
  // Detalhes da proposta (contato/telefone/PDF) — mantém habilitado enquanto o
  // compartilhamento estiver aberto, pois o modal de sucesso é fechado ao compartilhar.
  const { data: proposalData } = useProposalDetails(
    proposal?.id ?? null,
    (open || shareOpen) && !!proposal?.id,
  );
  const { getPdfSignedUrl } = useProposalPdf();

  const proposalHeader = proposalData?.proposal;
  const proposalContext = proposalData?.context;
  const pdfReady = proposalHeader?.pdf_status === 'ready' && !!proposalHeader?.pdf_storage_path;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-sm text-center" hideCloseButton>
          <DialogHeader className="flex flex-col items-center gap-4 pt-2">
            {/* Ícone de sucesso — círculo com gradiente verde (idêntico ao de Tarefas) */}
            <div className="flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-emerald-100 to-emerald-200 dark:from-emerald-900/40 dark:to-emerald-800/40 ring-4 ring-emerald-100 dark:ring-emerald-900/30">
              <Check className="w-8 h-8 text-emerald-600 dark:text-emerald-400" strokeWidth={3} />
            </div>

            <DialogTitle className="text-lg font-semibold">
              Negócio Criado com Sucesso!
            </DialogTitle>

            <DialogDescription className="sr-only">
              Negócio {dealTitle} criado
            </DialogDescription>
          </DialogHeader>

          {/* Destaque: título do negócio + ID truncado */}
          <div className="flex flex-col items-center gap-1 py-2">
            <span className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
              Negócio
            </span>
            <span className="text-xl font-semibold text-foreground line-clamp-2 px-4">
              {dealTitle}
            </span>
            {shortId && (
              <span className="text-xs font-mono text-muted-foreground mt-1">
                #{shortId}
              </span>
            )}
            {/* v1.1.0: proposta gerada automaticamente */}
            {proposal?.id && (
              <span className="mt-2 text-xs text-muted-foreground">
                Proposta{' '}
                <span className="font-medium text-foreground">
                  {proposalHeader?.proposal_number ?? '—'}
                </span>{' '}
                gerada
              </span>
            )}
          </div>

          {/* Texto descritivo */}
          <p className="text-sm text-muted-foreground px-4">
            O negócio foi criado e já está disponível no sistema. Você pode
            visualizá-lo para adicionar mais detalhes ou andamentos.
          </p>

          {/* Botões de ação */}
          <div className="flex flex-col gap-2 pt-2 pb-1">
            <Button onClick={onViewDeal} className="w-full gap-2">
              <Eye className="w-4 h-4" />
              Visualizar Negócio
            </Button>
            {proposal?.id && (
              <Button
                variant="secondary"
                onClick={() => {
                  // Um modal por vez: fecha o de sucesso antes de abrir o compartilhamento
                  onOpenChange(false);
                  setShareOpen(true);
                }}
                disabled={!proposalHeader}
                className="w-full gap-2"
              >
                <Share2 className="w-4 h-4" />
                Compartilhar proposta
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="w-full"
            >
              Fechar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Compartilhamento da proposta (mesmo modal usado no detalhe da proposta) */}
      {proposal?.id && proposalHeader && (
        <ProposalShareDialog
          open={shareOpen}
          onOpenChange={setShareOpen}
          getPdfSignedUrl={getPdfSignedUrl}
          onOpenConversation={(conversationId) => setChatConversationId(conversationId)}
          context={{
            proposalId: proposalHeader.id,
            proposalNumber: proposalHeader.proposal_number,
            total: proposalHeader.total,
            currency: proposalHeader.currency,
            expiresAt: proposalHeader.expires_at,
            dealTitle: proposalContext?.deal_title ?? dealTitle,
            contactId: proposalContext?.contact_id ?? null,
            contactName: proposalContext?.contact_name ?? null,
            contactPhone: proposalContext?.contact_phone ?? null,
            publicUrl: proposal.publicUrl ?? null,
            pdfStoragePath: pdfReady ? proposalHeader.pdf_storage_path : null,
          }}
        />
      )}

      {/* Conversa aberta a partir do compartilhamento */}
      {chatConversationId && (
        <ChatConversationModal
          open={!!chatConversationId}
          onOpenChange={(isOpen) => !isOpen && setChatConversationId(null)}
          conversationId={chatConversationId}
        />
      )}
    </>
  );
}
